/**
 * angular-admin-oskey-io
 *
 * @license
 * Copyright (c) 2021-2025, OSKEY SAS. All rights reserved.
 *
 * The source code is protected under international copyright law.
 * All rights reserved and protected by the copyright holders.
 *
 * The source code is confidential and only available to authorized individuals with the permission of the copyright holders.
 * If you encounter this this source code and do not have permission, please contact the copyright holders and delete this file.
 */
import { inject, Injectable } from '@angular/core';
import { OSKHttpsSuccessResponse } from '@oskey/core';
import { OSKDocumentList } from '@oskey/core/types';
import { OSKFirebaseHttpsService } from '@oskey/firebase';
import { OSKBuilding } from 'src/app/core/types/building/building.type';
import {
  OSKCreateProperty,
  OSKGetPropertyDashboardStaticsResponse,
  OSKProperty
} from '../types/organization-property.typs';

@Injectable({
  providedIn: 'root'
})
export class OSKOrganizationPropertyService {
  private readonly firebaseHttps: OSKFirebaseHttpsService = inject(OSKFirebaseHttpsService);

  public getAllProperties(
    organizationId: string,
    entityId: string
  ): Promise<OSKHttpsSuccessResponse<OSKDocumentList<OSKProperty>>> {
    return this.firebaseHttps.call<
      { organizationId: string; entityId: string },
      OSKDocumentList<OSKProperty>
    >('organization-getAllProperties', { organizationId, entityId });
  }

  public createProperty(
    property: OSKCreateProperty
  ): Promise<OSKHttpsSuccessResponse<OSKProperty>> {
    return this.firebaseHttps.call<OSKCreateProperty, OSKProperty>(
      'organization-createProperty',
      property
    );
  }

  public getPropertyById(
    organizationId: string,
    propertyId: string
  ): Promise<OSKHttpsSuccessResponse<OSKProperty>> {
    return this.firebaseHttps.call<{ organizationId: string; propertyId: string }, OSKProperty>(
      'organization-getPropertyById',
      { organizationId, propertyId }
    );
  }

  public updateProperty(
    organizationId: string,
    propertyId: string,
    update: Partial<OSKProperty>
  ): Promise<OSKHttpsSuccessResponse<OSKProperty>> {
    return this.firebaseHttps.call<
      { organizationId: string; propertyId: string; update: Partial<OSKProperty> },
      OSKProperty
    >('organization-updateProperty', { organizationId, propertyId, update });
  }

  public deleteProperty(
    organizationId: string,
    propertyId: string
  ): Promise<OSKHttpsSuccessResponse<void>> {
    return this.firebaseHttps.call<{ organizationId: string; propertyId: string }, void>(
      'organization-deleteProperty',
      { organizationId, propertyId }
    );
  }
  public getPropertyDashboardStatics(
    organizationId: string,
    propertyId: string
  ): Promise<OSKHttpsSuccessResponse<OSKGetPropertyDashboardStaticsResponse>> {
    return this.firebaseHttps.call<
      { organizationId: string; propertyId: string },
      OSKGetPropertyDashboardStaticsResponse
    >('organization-getPropertyDashboardStatics', { organizationId, propertyId });
  }

  public assignBuildingToProperty(
    organizationId: string,
    oldPropertyId: string,
    newPropertyId: string,
    buildingId: string,
    buildingData: Partial<OSKBuilding>
  ): Promise<OSKHttpsSuccessResponse<void>> {
    return this.firebaseHttps.call<
      {
        organizationId: string;
        oldPropertyId: string;
        newPropertyId: string;
        buildingId: string;
        buildingData: Partial<OSKBuilding>;
      },
      void
    >('organization-assigningBuildingToProperty', {
      organizationId,
      oldPropertyId,
      newPropertyId,
      buildingId,
      buildingData
    });
  }
}
